import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { cashflowApi } from '../services/cashflowApi';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { Cashflow, FLOW_KINDS, FlowKind, KIND_META } from '../types/cashflow';
import { CashflowFormModal } from '../components/CashflowFormModal';
import { KIND_STYLE, Participant, Vehicle, won } from '../types/vehicle';
import { Deal } from '../types/deal';
import { Company } from '../types/company';
import { Tour, TourButton, TourStep } from '../components/Tour';

/**
 * 원장 — 모든 돈이 한 줄씩.
 *
 * 이 화면이 이 도구의 심장이다. 여기가 비면 수익률이 아예 안 나온다.
 * ⚠️ 날짜는 **통장 입출금일**이다. 계약일을 적으면 수익률이 틀린다.
 */
export const LedgerPage: React.FC = () => {
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [parts, setParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);
  const [params] = useSearchParams();
  // 자금원 상세의 「전부 보기」가 ?vehicle= 로 들어온다
  const [vehicleFilter, setVehicleFilter] = useState<number | null>(
    params.get('vehicle') ? Number(params.get('vehicle')) : null,
  );
  const [kindFilter, setKindFilter] = useState<FlowKind | null>(null);
  // 연도 — 결산 · 반기 보고 때 한 해만 본다
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  // 줄을 누르면 오른쪽 패널로 기록 수정(UI 규칙). 지우기도 그 안에서
  const [editing, setEditing] = useState<Cashflow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, v, d, c] = await Promise.all([
        cashflowApi.list(), vehicleApi.list(), dealApi.list(), companyApi.list(),
      ]);
      setFlows(f); setVehicles(v); setDeals(d); setCompanies(c);
      const ps = await Promise.all(v.map((x) => vehicleApi.participants(x.id)));
      setParts(ps.flat());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vName = (id: number) => vehicles.find((v) => v.id === id)?.name ?? '—';
  const vKind = (id: number) => vehicles.find((v) => v.id === id)?.kind;
  const pName = (id: number | null) => (id === null ? null : parts.find((p) => p.id === id)?.name ?? null);
  const dLabel = (id: number | null) => {
    if (id === null) return null;
    const d = deals.find((x) => x.id === id);
    if (!d) return null;
    return companies.find((c) => c.id === d.companyId)?.name ?? null;
  };

  const shown = useMemo(() => flows.filter((f) => {
    if (vehicleFilter !== null && f.vehicleId !== vehicleFilter) return false;
    if (kindFilter !== null && f.kind !== kindFilter) return false;
    if (yearFilter !== null && Number(f.occurredOn.slice(0, 4)) !== yearFilter) return false;
    return true;
  }), [flows, vehicleFilter, kindFilter, yearFilter]);
  const years = useMemo(() => Array.from(new Set(flows.map((f) => Number(f.occurredOn.slice(0, 4))))).sort((a, b) => b - a), [flows]);

  const inSum = shown.filter((f) => f.direction === 'in').reduce((s, f) => s + f.amount, 0);
  const outSum = shown.filter((f) => f.direction === 'out').reduce((s, f) => s + f.amount, 0);


  const chip = (on: boolean) =>
    `px-2.5 py-1 rounded-lg text-xs font-medium border transition ${
      on ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
    }`;

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'intro',
      target: '[data-tour="title"]',
      title: '원장',
      content: '출자 · 투자 · 회수 · 배당 · 분배 · 보수 · 비용, 모든 돈이 여기 한 줄씩 들어갑니다. 대시보드의 숫자는 전부 이 화면에서 나옵니다.',
    },
    {
      name: 'add',
      target: '[data-tour="add"]',
      title: '기록 추가',
      content: '통장에서 돈이 움직인 날 적습니다. 날짜는 계약일이 아니라 입출금일입니다. 계약일을 적으면 IRR이 틀립니다.',
      placement: 'left',
    },
    {
      name: 'filter',
      target: '[data-tour="filter"]',
      title: '거르기',
      content: '자금원 · 종류 · 연도로 거릅니다. 예를 들어 「보수」만 고르면 GRK 매출이 된 돈만 봅니다.',
    },
    {
      name: 'sums',
      target: '[data-tour="sums"]',
      title: '합계',
      content: '지금 거른 줄의 입금 · 출금 합계와 잔액입니다. 자금원 하나 · 전체 연도로 보면 잔액이 그 통장 잔액과 같아야 합니다 — 다르면 빠진 기록이 있습니다.',
    },
  ], []);

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div data-tour="title">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            원장
            <TourButton onClick={() => setTourRun(true)} />
          </h1>
        </div>
        <button data-tour="add" onClick={() => setOpen(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex-shrink-0">
          <Plus size={13} /> 기록 추가
        </button>
      </div>

      <div data-tour="filter" className="bg-white border border-slate-200 rounded-lg px-4 py-3 mb-4 space-y-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-400 mr-1 w-12">자금원</span>
          <button className={chip(vehicleFilter === null)} onClick={() => setVehicleFilter(null)}>전체</button>
          {vehicles.map((v) => (
            <button key={v.id} className={chip(vehicleFilter === v.id)}
              onClick={() => setVehicleFilter(vehicleFilter === v.id ? null : v.id)}>{v.name}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-400 mr-1 w-12">종류</span>
          <button className={chip(kindFilter === null)} onClick={() => setKindFilter(null)}>전체</button>
          {FLOW_KINDS.map((k) => (
            <button key={k} className={chip(kindFilter === k)}
              onClick={() => setKindFilter(kindFilter === k ? null : k)}>{KIND_META[k].label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-400 mr-1 w-12">연도</span>
          <button className={chip(yearFilter === null)} onClick={() => setYearFilter(null)}>전체</button>
          {years.map((y) => (
            <button key={y} className={chip(yearFilter === y)} onClick={() => setYearFilter(yearFilter === y ? null : y)}>{y}</button>
          ))}
        </div>
      </div>

      <div data-tour="sums" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
          <div className="text-xs text-slate-400">입금 합계</div>
          <div className="mt-0.5 text-lg font-bold text-emerald-700 tabular-nums">{won(inSum)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
          <div className="text-xs text-slate-400">출금 합계</div>
          <div className="mt-0.5 text-lg font-bold text-slate-900 tabular-nums">{won(outSum)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
          <div className="text-xs text-slate-400">잔액 <span className="text-slate-300">— 통장과 대조</span></div>
          <div className={`mt-0.5 text-lg font-bold tabular-nums ${inSum - outSum < 0 ? 'text-red-600' : 'text-indigo-700'}`}>{won(inSum - outSum)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
          <div className="text-xs text-slate-400">건수</div>
          <div className="mt-0.5 text-lg font-bold text-slate-900 tabular-nums">{shown.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" /> 불러오는 중...
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
          <p className="text-sm text-slate-500">조건에 맞는 기록이 없습니다.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5">날짜</th>
                <th className="text-left font-medium px-2 py-2.5">종류</th>
                <th className="text-left font-medium px-2 py-2.5">자금원</th>
                <th className="text-left font-medium px-2 py-2.5">거래 상대</th>
                <th className="text-right font-medium px-4 py-2.5">금액</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => {
                const meta = KIND_META[f.kind];
                const other = dLabel(f.dealId) ?? pName(f.participantId);
                const k = vKind(f.vehicleId);
                return (
                  <tr key={f.id} onClick={() => setEditing(f)}
                    className={`border-b border-slate-100 last:border-b-0 align-top cursor-pointer hover:bg-slate-50 ${editing?.id === f.id ? 'bg-indigo-50/60' : ''}`}>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap tabular-nums">
                      {f.occurredOn.replace(/-/g, '.')}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${meta.style}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-xs whitespace-nowrap">
                      <span className="text-slate-700">{vName(f.vehicleId)}</span>
                      {k && (
                        <span className={`ml-1 rounded-full px-1.5 py-0.5 text-xs ${KIND_STYLE[k]}`}>
                          {k === 'own' ? '고유' : '위탁'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-xs text-slate-600">
                      {other ?? <span className="text-slate-300">—</span>}
                      {f.memo && <div className="text-slate-400">{f.memo}</div>}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium whitespace-nowrap tabular-nums ${
                      f.direction === 'in' ? 'text-emerald-700' : 'text-slate-900'
                    }`}>
                      {f.direction === 'in' ? '+' : '−'}{won(f.amount).slice(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />

      <CashflowFormModal open={open} vehicles={vehicles} parts={parts} deals={deals} companies={companies}
        onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      <CashflowFormModal open={editing !== null} editing={editing} vehicles={vehicles} parts={parts} deals={deals} companies={companies}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
};
